/**
 * Installs global capture of silent failures — the breakage users never report.
 * Wraps window.fetch to record non-2xx API responses and network errors, and
 * listens for uncaught JS errors / unhandled promise rejections. All routed to
 * the backend EventLog via api/events. Call once at startup (idempotent).
 */
import { recordError } from "../api/events"

let installed = false

// The browser aborts every in-flight request when the page goes away, and those
// aborts surface here as ordinary "Failed to fetch" network errors. They are not
// failures — a user closing a tab or following a link mid-load is routine, and
// reporting them would bury the real breakage in noise (and page anyone we alert).
let unloading = false

// Reduce a full URL to its path so reports group cleanly and don't leak the
// origin. Falls back to the raw string if it can't be parsed.
function toPath(url: string): string {
  try {
    return new URL(url, window.location.origin).pathname
  } catch {
    return url
  }
}

/**
 * Pull the real reason out of a failed response. FastAPI puts it in a `detail`
 * field — either a string we raised, or the array of field errors a 422 produces.
 * Without this we'd store res.statusText, which is only ever the generic HTTP
 * name ("Bad Request") and tells the reader nothing about what actually broke.
 *
 * Reads a clone so the calling code still gets an unconsumed body.
 */
async function failureReason(res: Response): Promise<string | null> {
  try {
    const body = await res.clone().json()
    const detail = body?.detail
    if (typeof detail === "string") return detail
    // 422 validation errors arrive as [{ loc, msg, type }, ...].
    if (Array.isArray(detail)) {
      const messages = detail.map((entry) => entry?.msg).filter(Boolean)
      if (messages.length > 0) return messages.join("; ")
    }
  } catch {
    // Body wasn't JSON (an HTML error page, an empty 502). Fall back to statusText.
  }
  return null
}

export function installUsageTracking(): void {
  if (installed) return
  installed = true

  // pagehide covers tab close, reload, and navigation away — including the
  // bfcache path, which beforeunload alone misses.
  window.addEventListener("pagehide", () => {
    unloading = true
  })
  window.addEventListener("beforeunload", () => {
    unloading = true
  })

  const originalFetch = window.fetch.bind(window)
  window.fetch = async (...args: Parameters<typeof window.fetch>): Promise<Response> => {
    const input = args[0]
    const url = typeof input === "string" ? input : input instanceof Request ? input.url : String(input)
    // Never report on the events endpoints themselves — avoids a feedback loop.
    const isEventsCall = url.includes("/events/")
    try {
      const res = await originalFetch(...args)
      if (res.status >= 400 && !isEventsCall) {
        // Not awaited: the clone happens synchronously inside failureReason, so
        // the caller gets its response back without waiting on our bookkeeping.
        void failureReason(res).then((reason) => {
          recordError({
            event_type: "api_error",
            path: toPath(url),
            status_code: res.status,
            message: reason ?? res.statusText ?? null,
          })
        })
      }
      return res
    } catch (err) {
      // An AbortError is a deliberate cancellation (an AbortController, or the
      // page tearing down), never a failure worth reporting.
      const aborted = err instanceof DOMException && err.name === "AbortError"
      if (!isEventsCall && !unloading && !aborted) {
        recordError({
          event_type: "api_error",
          path: toPath(url),
          message: err instanceof Error ? err.message : "Network error",
        })
      }
      throw err
    }
  }

  window.addEventListener("error", (e) => {
    recordError({
      event_type: "client_error",
      path: window.location.pathname,
      message: (e.message || "Uncaught error").slice(0, 500),
    })
  })

  window.addEventListener("unhandledrejection", (e) => {
    // The same teardown aborts reach us here whenever a caller didn't catch the
    // rejection the fetch wrapper re-threw. Suppress them for the same reason.
    if (unloading) return
    const reason = (e as PromiseRejectionEvent).reason
    const message = reason instanceof Error ? reason.message : String(reason)
    recordError({
      event_type: "client_error",
      path: window.location.pathname,
      message: (message || "Unhandled rejection").slice(0, 500),
    })
  })
}
